import { useState } from 'react';
import { AlertCircle, CalendarClock, X } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

// Mirrors backend OrderHoldService. The server re-checks the LIVE pharmacy
// status before acting, so this is only for enabling the button and explaining
// why it's disabled.
const MAX_HOLD_DAYS = 30;
const CLINIC_DELAYABLE = new Set(['TO_BE_FILLED']);
const ADMIN_DELAYABLE = new Set(['TO_BE_FILLED', 'PARTIAL_FILL', 'COMPLETE_FILLED', 'LABEL_CREATED', 'ISSUE_IN_SHIPPING']);

export interface OrderHold {
  state: 'pending' | 'active' | 'rejected' | 'released';
  via?: 'direct' | 'pull_live';
  holdUntil: string;
  reason?: string;
  requestedByRole?: string;
  decisionNote?: string;
  resolvedAt?: string;
  releasedAt?: string;
  releaseCause?: 'scheduled' | 'manual' | 'pharmacy';
}

export interface OrderHoldTarget {
  _id: string;
  status: string;
  pharmacyOrderId?: string;
  lastPharmacyStatus?: string;
  hold?: OrderHold | null;
}

function ymdFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatHoldDate(value: string): string {
  // Stored as UTC midnight of a calendar day; render that day, not the local shift.
  return new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
}

function delayBlockReason(order: OrderHoldTarget, isAdmin: boolean): string | null {
  if (!order.pharmacyOrderId) return 'The order has not reached the pharmacy yet.';
  if (order.hold?.state === 'pending') return 'A delay is waiting on pharmacist review.';
  const ph = order.lastPharmacyStatus || '';
  if (ph === 'ON_HOLD') return order.hold?.state === 'active' ? null : 'The order is already on hold at the pharmacy.';
  if (isAdmin) return ADMIN_DELAYABLE.has(ph) ? null : 'The order has left the pharmacy.';
  if (CLINIC_DELAYABLE.has(ph)) return null;
  return ['PARTIAL_FILL', 'COMPLETE_FILLED', 'PULLED', 'LABEL_CREATED', 'ISSUE_IN_SHIPPING'].includes(ph)
    ? 'The order has already been filled, so it can no longer be delayed. Contact support if it needs to be held.'
    : 'The order has left the pharmacy, so it can no longer be delayed.';
}

/**
 * Delay / release controls for one order, shared by the clinic order view and
 * the admin order view. `onChanged` should refetch the order.
 */
export function OrderHoldPanel({
  order,
  isAdmin,
  onChanged
}: {
  order: OrderHoldTarget;
  isAdmin: boolean;
  // Receives the server's updated order (hold + lastPharmacyStatus) when known.
  onChanged: (updated?: { hold?: OrderHold | null; lastPharmacyStatus?: string }) => void;
}) {
  const [showModal, setShowModal] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const hold = order.hold;
  const active = hold?.state === 'active';
  // Admin-placed holds are the admin's to change (server enforces this too).
  const adminOwned = !isAdmin && hold?.requestedByRole === 'admin';
  const blockReason = adminOwned && active
    ? 'An admin placed this hold. Contact support to change it.'
    : delayBlockReason(order, isAdmin);
  // Nothing to say on orders that never reached the pharmacy or are long gone.
  const relevant = !!order.pharmacyOrderId && !['cancelled', 'voided', 'delivered'].includes(order.status);
  if (!relevant && !hold?.state) return null;

  const release = async () => {
    setReleasing(true);
    try {
      const res = await apiClient.releaseOrderHold(order._id);
      if (res.success) {
        toast.success(res.message || 'Hold released.');
        onChanged(res.data);
      } else {
        toast.error(res.error || 'Could not release the hold.');
      }
    } catch {
      toast.error('Could not release the hold.');
    } finally {
      setReleasing(false);
    }
  };

  return (
    <div className="space-y-2">
      {active && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="inline-flex items-center gap-1.5">
            <CalendarClock className="h-4 w-4" />
            Delayed until <strong>{formatHoldDate(hold!.holdUntil)}</strong>
            {hold!.reason ? <span className="text-amber-700">· {hold!.reason}</span> : null}
          </span>
          {!adminOwned && <button
            onClick={release}
            disabled={releasing}
            className="rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            {releasing ? 'Releasing…' : 'Release now'}
          </button>}
        </div>
      )}
      {hold?.state === 'pending' && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          Delay until <strong>{formatHoldDate(hold.holdUntil)}</strong> is waiting on pharmacist review.
        </div>
      )}
      {hold?.state === 'rejected' && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          The pharmacy declined the delay{hold.decisionNote ? `: ${hold.decisionNote}` : '.'}
        </div>
      )}

      {relevant && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => !blockReason && setShowModal(true)}
            disabled={!!blockReason}
            title={blockReason || undefined}
            className={`inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium ${
              blockReason
                ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                : 'cursor-pointer border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
            }`}
          >
            <CalendarClock className="h-4 w-4" />
            {active ? 'Change delay date' : 'Delay shipment'}
          </button>
          {blockReason && !hold?.state && <span className="text-xs italic text-gray-500">{blockReason}</span>}
        </div>
      )}

      {showModal && (
        <DelayOrderModal
          orderId={order._id}
          initialDate={active ? hold!.holdUntil.slice(0, 10) : undefined}
          isAdmin={isAdmin}
          onClose={() => setShowModal(false)}
          onSuccess={(updated) => {
            setShowModal(false);
            onChanged(updated);
          }}
        />
      )}
    </div>
  );
}

function DelayOrderModal({
  orderId,
  initialDate,
  isAdmin,
  onClose,
  onSuccess
}: {
  orderId: string;
  initialDate?: string;
  isAdmin: boolean;
  onClose: () => void;
  onSuccess: (updated?: any) => void;
}) {
  const minDate = ymdFromToday(1);
  const maxDate = ymdFromToday(MAX_HOLD_DAYS);
  const [holdUntil, setHoldUntil] = useState(initialDate || ymdFromToday(7));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reasonOk = reason.trim().length >= 5;
  const dateOk = holdUntil >= minDate && holdUntil <= maxDate;

  const submit = async () => {
    if (!reasonOk || !dateOk) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.holdOrder(orderId, { holdUntil, reason: reason.trim() });
      if (res.success) {
        toast.success(res.message || 'Order delayed.');
        onSuccess(res.data);
      } else {
        setError(res.error || 'The order could not be delayed.');
      }
    } catch {
      setError('The order could not be delayed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] h-full w-full overflow-y-auto bg-gray-600 bg-opacity-50" onClick={onClose}>
      <div
        className="relative top-16 mx-auto w-full max-w-md rounded-md border bg-white p-5 shadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-amber-600" />
            <h3 className="text-lg font-medium text-gray-900">Delay shipment</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mb-4 text-sm text-gray-600">
          {isAdmin
            ? 'Files a pull-live delay with the pharmacy. Once a shipping label exists, the pharmacist must approve it.'
            : 'The pharmacy will hold this order and resume it on the date you pick. Orders that have already been filled can’t be delayed.'}
        </p>

        {error && (
          <div className="mb-4 flex gap-2 rounded-md border border-red-200 bg-red-50 p-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="hold-until">Resume on</label>
        <input
          id="hold-until"
          type="date"
          value={holdUntil}
          min={minDate}
          max={maxDate}
          onChange={e => setHoldUntil(e.target.value)}
          className="mb-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
        <p className="mb-4 text-xs text-gray-500">Up to {MAX_HOLD_DAYS} days from today.</p>

        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="hold-reason">Reason</label>
        <textarea
          id="hold-reason"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="e.g. Patient travelling until the 20th"
          className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        />

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!reasonOk || !dateOk || submitting}
            className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {submitting ? 'Delaying…' : 'Delay order'}
          </button>
        </div>
      </div>
    </div>
  );
}
